import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import Dashboard from 'components/Dashboard';
import RenderReadinessProvider from 'contextProviders/RenderReadinessProvider';
import useLegacySavedFilter from 'hooks/useLegacySavedFilter';
import usePrintReady, { REPORT_MAX_WAIT_MS } from 'hooks/usePrintReady';

import { useDashboard } from '../DashboardShow/DashboardShow';

// The dashboard's own wrapper is a viewport-height scroll container, so we don't
// flatten heights (that collapses the layout). The renderer grows the viewport
// to the content height so it all fits. Here we just strip the body margin,
// ensure a solid background, and hide the variable-filter controls bar (it
// carries the EXCLUDE_HEIGHT class) so the PDF is the gadget grid only.
const PrintGlobalStyle = createGlobalStyle`
  body { margin: 0; background: var(--color-bg-primary); }
  #print-dashboard-root [class*="exclude-height-"] { display: none !important; }
`;

// Fixed render width for a consistent layout regardless of the runner's viewport.
const PrintRoot = styled.div`
  width: 1280px;
  background: var(--color-bg-primary);
`;

const PrintDashboardContent = () => {
  // This screen isn't mounted under a :dashboardId <Route>, so derive the id
  // from the path rather than useParams (which would be empty here).
  const { pathname, search } = useLocation();
  const dashboardId = pathname.split('/print/dashboards/')[1]?.split(/[?/]/)[0];
  const savedFilterId =
    new URLSearchParams(search).get('savedFilterId') ?? undefined;

  const { dashboard, isLoading: dashboardLoading } = useDashboard(dashboardId);
  const { savedFilter, isLoading: savedFilterLoading } =
    useLegacySavedFilter(savedFilterId);

  // Replay the scheduled variable-filter snapshot by overriding the dashboard's
  // persisted variableDrillDowns. The dashboard container initialises its live
  // variable filters from this value, so no other wiring is needed.
  const dashboardWithSnapshot = useMemo(() => {
    if (!dashboard) return undefined;
    if (savedFilter?.variableDrillDowns) {
      return {
        ...dashboard,
        variableDrillDowns: savedFilter.variableDrillDowns,
      };
    }
    return dashboard;
  }, [dashboard, savedFilter]);

  // Ready once the dashboard + snapshot have loaded and the gadgets have
  // settled (see usePrintReady).
  const ready = usePrintReady({
    maxWaitMs: REPORT_MAX_WAIT_MS,
    isContentReady:
      !dashboardLoading && !savedFilterLoading && !!dashboardWithSnapshot,
  });

  return (
    <PrintRoot
      id="print-dashboard-root"
      data-print-ready={ready ? 'true' : undefined}
    >
      <PrintGlobalStyle />
      {dashboardWithSnapshot && (
        <Dashboard
          dashboard={dashboardWithSnapshot}
          key={dashboardWithSnapshot.id}
          forceHeadlessRender
        />
      )}
    </PrintRoot>
  );
};

// Gadgets report their requests to RenderReadinessProvider, which the ready
// check waits on and the renderer reads
const PrintDashboardShow = () => (
  <RenderReadinessProvider>
    <PrintDashboardContent />
  </RenderReadinessProvider>
);

export default PrintDashboardShow;
